import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCreditTopUp } from "hooks/useCreditTopUp";
import { useToast } from "hooks";
import { CREDITS_QUERY_KEY } from "hooks/useCredits";
import { CreditCheckoutModal } from "./CreditCheckoutModal";
import { PaymentEmbed } from "./PaymentEmbed";
import type { CheckoutContextValue, CheckoutFlowState, CheckoutTopupData } from "./types";

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

const CALLBACK_URL = `${window.location.origin}/pricing`;

export const CheckoutProvider = ({ children }: { children: ReactNode }) => {
  const [flowState, setFlowState] = useState<CheckoutFlowState>({ step: "idle" });
  const [topup, setTopup] = useState<CheckoutTopupData | null>(null);
  const [paymentURL, setPaymentURL] = useState("");
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  const { topUpCreditsAsync } = useCreditTopUp();

  const openCheckout = useCallback((data: CheckoutTopupData, onSuccess?: () => void) => {
    setTopup(data);
    setOnSuccessCallback(() => onSuccess);
    setFlowState({ step: "checkout", data });
  }, []);

  const closeCheckout = useCallback(() => {
    setFlowState({ step: "idle" });
    setTopup(null);
    setPaymentURL("");
  }, []);

  const proceedToPayment = useCallback(async () => {
    if (!topup) return;

    setFlowState({ step: "processing" });

    try {
      const response: any = await topUpCreditsAsync({
        credits: topup.credits,
        callback_url: CALLBACK_URL,
      });

      // The hook returns response.data, so one less nesting level than axios.
      const authorizationUrl =
        response?.data?.data?.authorization_url ?? response?.data?.authorization_url;

      if (!authorizationUrl) {
        throw new Error("Could not retrieve payment URL from response.");
      }

      setPaymentURL(authorizationUrl);
      setFlowState({ step: "payment", paymentURL: authorizationUrl });
    } catch (error: any) {
      const message = error?.response?.data?.message ?? error?.message ?? "Something went wrong.";
      openToast({
        show: true,
        heading: "Error",
        text: message,
        type: "error",
        timeOut: 5000,
      });
      // Back to the summary so the user can retry.
      setFlowState({ step: "checkout", data: topup });
    }
  }, [topup, topUpCreditsAsync, openToast]);

  const handlePaymentSuccess = useCallback(async () => {
    openToast({
      show: true,
      heading: "Success",
      text: "Payment completed successfully!",
      type: "success",
      timeOut: 5000,
    });

    // Refresh every surface that reads the balance.
    queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['creditUsage'] });
    queryClient.invalidateQueries({ queryKey: ['billingHistory'] });

    onSuccessCallback?.();
    closeCheckout();
  }, [openToast, queryClient, onSuccessCallback, closeCheckout]);

  const handlePaymentClose = useCallback(() => {
    // User closed the payment window — go back to the summary.
    if (topup) {
      setFlowState({ step: "checkout", data: topup });
    } else {
      closeCheckout();
    }
  }, [topup, closeCheckout]);

  const isProcessing = flowState.step === "processing";
  const showCheckoutModal = flowState.step === "checkout" || flowState.step === "processing";
  const showPaymentEmbed = flowState.step === "payment";

  return (
    <CheckoutContext.Provider value={{ flowState, openCheckout, closeCheckout, proceedToPayment }}>
      {children}

      {showCheckoutModal && topup && (
        <CreditCheckoutModal
          data={topup}
          isProcessing={isProcessing}
          onProceed={proceedToPayment}
          onClose={closeCheckout}
        />
      )}

      <PaymentEmbed
        show={showPaymentEmbed}
        paymentURL={paymentURL}
        close={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
        onDecline={handlePaymentClose}
        onClose={handlePaymentClose}
      />
    </CheckoutContext.Provider>
  );
};

export const useCheckout = (): CheckoutContextValue => {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
};
